import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useAppUi } from '../app/useAppUi';

const isPrimitive = (v) =>
  v === null ||
  v === undefined ||
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean';

const formatValue = (v) => {
  if (v === null) return 'null';
  if (v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
};

export function RecordDialog({ open, title, record, onClose }) {
  const { t } = useAppUi();
  const [tab, setTab] = React.useState(0);
  const json = React.useMemo(() => JSON.stringify(record ?? null, null, 2), [record]);

  React.useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  const entries = React.useMemo(() => {
    const r = record && typeof record === 'object' ? record : null;
    if (!r) return [];
    return Object.entries(r).sort(([a], [b]) => a.localeCompare(b));
  }, [record]);

  const overviewRows = entries.filter(([, v]) => isPrimitive(v));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label={t('recordDialog.overview')} />
          <Tab label={t('recordDialog.json')} />
        </Tabs>

        {tab === 0 ? (
          <TableContainer
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableBody>
                {overviewRows.length ? (
                  overviewRows.map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell
                        sx={{
                          width: 220,
                          fontWeight: 800,
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                        }}
                      >
                        {k}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'inherit' }}>
                          {formatValue(v)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('recordDialog.noPrimitives')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1.5,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {json}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(json);
            } catch {
              // ignore
            }
          }}
        >
          {t('common.copyJson')}
        </Button>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

